import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Socket } from "socket.io-client"

import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import {
  disconnectSocket,
  initiateSocketConnection,
  subscribeToChat
} from "@/modules/helpers/socket"
import { Message } from "@/modules/types/index"

interface UseWSChatProps {
  data?: Message[]
  room?: string | number
  onSwitch?: () => void
  onUpdate?: () => void
}

/**
 * Hook for managing WebSocket chat connections
 * Handles socket connection, message subscriptions, and cleanup
 */
const useWSChat = ({ data, room, onSwitch, onUpdate }: UseWSChatProps) => {
  const refSocket = useRef<Socket | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const refMessages = useRef<Message[]>([])
  const refChannelName = useRef<string | null>(null)
  const refCallback = useRef<((err: any, data: { data: { messages: Message[] } }) => void) | null>(
    null
  )
  const isConnectingRef = useRef<boolean>(false)
  const { user } = useCurrentUser()

  // Memoize callbacks to prevent unnecessary re-renders
  const onSwitchRef = useRef(onSwitch)
  const onUpdateRef = useRef(onUpdate)

  useEffect(() => {
    onSwitchRef.current = onSwitch
    onUpdateRef.current = onUpdate
  }, [onSwitch, onUpdate])

  /**
   * Updates messages list, avoiding duplicates by message ID
   */
  const updateMessages = useCallback((message: Message) => {
    const newMessage = { ...message, isNew: true }

    const messageMap = new Map<string | number, Message>()

    const generateKey = (msg: Message) => {
      const id = (msg as any)?.id
      if (id) return id
      const content = msg.content || ""
      const createdAt = (msg as any)?.createdAt || Date.now()
      const attachments = Array.isArray(msg.attachmentUrl)
        ? msg.attachmentUrl.join(",")
        : msg.attachmentUrl || ""
      return `${content}_${createdAt}_${attachments}`
    }

    // Add new message first (so it appears at top)
    const messageKey = generateKey(newMessage)
    messageMap.set(messageKey, newMessage)

    // Add existing messages, skipping duplicates
    refMessages.current.forEach((msg) => {
      const key = generateKey(msg)
      if (!messageMap.has(key)) {
        messageMap.set(key, msg)
      }
    })

    const arrayMessages = Array.from(messageMap.values())
    refMessages.current = arrayMessages
    setMessages(arrayMessages)
  }, [])

  /**
   * Cleanup socket connection and event listeners
   */
  const cleanupSocket = useCallback(() => {
    const socket = refSocket.current
    const channelName = refChannelName.current

    // Reset connecting flag
    isConnectingRef.current = false

    if (socket && channelName) {
      // Remove ALL listeners for this channel to ensure no duplicates
      try {
        socket.removeAllListeners(channelName)
      } catch (error) {
        console.warn("Error removing socket listeners:", error)
      }
    }

    // Disconnect socket if it exists
    if (socket) {
      try {
        // Remove all listeners before disconnecting
        socket.removeAllListeners()
        disconnectSocket({ current: socket } as any)
      } catch (error) {
        console.warn("Error disconnecting socket:", error)
      }
      refSocket.current = null
    }

    // Reset refs
    refChannelName.current = null
    refCallback.current = null
  }, [])

  /**
   * Initialize socket connection and subscribe to chat messages
   */
  useEffect(() => {
    // Prevent multiple simultaneous connections
    if (isConnectingRef.current) {
      console.warn("Socket connection already in progress, skipping...")
      return
    }

    // Cleanup previous connection if room or user changes
    cleanupSocket()

    if (!room) {
      return
    }

    const channelName = `listenChat_${room}`
    refChannelName.current = channelName

    // Create callback function that uses refs to avoid stale closures
    const handleMessage = (err: any, data: { data: { messages: Message[] } }) => {
      if (err) {
        console.error("WebSocket error:", err)
        return
      }

      onSwitchRef.current?.()

      if (data?.data?.messages?.[0]) {
        updateMessages(data.data.messages[0])
      }

      onUpdateRef.current?.()
    }

    refCallback.current = handleMessage

    // Set connecting flag
    isConnectingRef.current = true

    // Initialize socket connection
    initiateSocketConnection(refSocket, room)
      .then(() => {
        isConnectingRef.current = false

        // Double check socket exists and channel hasn't changed
        if (refSocket.current && refChannelName.current === channelName) {
          // Remove any existing listeners for this channel first
          if (refSocket.current) {
            refSocket.current.removeAllListeners(channelName)
          }

          subscribeToChat(refSocket, handleMessage, null, channelName)
          console.log("Socket connected and subscribed to:", channelName)
        }
      })
      .catch((error) => {
        isConnectingRef.current = false
        console.error("Failed to initialize socket connection:", error)
        refSocket.current = null
      })

    // Cleanup on unmount or dependency change
    return () => {
      cleanupSocket()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, user?.id])

  /**
   * Update messages when data prop changes
   * Using useMemo to avoid unnecessary re-renders
   */
  const dataStringified = useMemo(() => {
    if (!data) return null
    // Create a stable reference by stringifying only when data actually changes
    return JSON.stringify(
      data.map((msg) => ({
        id: (msg as any)?.id,
        content: msg.content,
        senderId: msg.senderId,
        createdAt: (msg as any)?.createdAt
      }))
    )
  }, [data])

  useEffect(() => {
    if (data && data.length > 0) {
      // Merge incoming data with existing messages to preserve optimistic updates
      const messageMap = new Map<string | number, Message>()

      // Add existing messages first (includes optimistic updates)
      refMessages.current.forEach((msg) => {
        const key =
          (msg as any)?.id ||
          `${msg.content}_${(msg as any)?.createdAt || 0}_${
            Array.isArray(msg.attachmentUrl) ? msg.attachmentUrl.join(",") : msg.attachmentUrl || ""
          }`
        messageMap.set(key, msg)
      })

      // Add/update with server data (authoritative source)
      data.forEach((msg) => {
        const key =
          (msg as any)?.id ||
          `${msg.content}_${(msg as any)?.createdAt || 0}_${
            Array.isArray(msg.attachmentUrl) ? msg.attachmentUrl.join(",") : msg.attachmentUrl || ""
          }`
        messageMap.set(key, msg)
      })

      const merged = Array.from(messageMap.values())
      refMessages.current = merged
      setMessages(merged)
    }
  }, [data, dataStringified])

  return { messages, updateMessages, refSocket }
}

export default useWSChat
